/**
 * VÉRAULT Member Suite — TransportationShelf (Wave 27.2 · Phase 4b).
 * ────────────────────────────────────────────────────────────────────
 * Flights · rail · transfers · drivers · ferries. Each card surfaces:
 *   timing · terminal/gate · confirmation · provider · status · notes
 *   · attachments (boarding passes, vouchers, receipts).
 *
 * Phase 4b polish:
 *   ▸ Multi-segment flights grouped under a parent card; segments
 *     render as quiet sub-rows with their own timing & gates.
 *   ▸ Terminal/gate display surfaces inline when present.
 *   ▸ Per-card attachments render as quiet download chips.
 */
import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Plane, Car, Train, Ship, Clock, Copy, ChevronDown, Phone, Paperclip, ArrowRight, AlertCircle } from "lucide-react";
import { withEventStates, stateLabel } from "../../../../utils/eventState";

function StateChip({ item }) {
  if (!item || !item._state) return null;
  const m = stateLabel(item);
  return (
    <span className={`font-mono text-[8.5px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full ${m.cls}`} data-testid={`transport-state-${item.id}`}>
      {m.label}
    </span>
  );
}

const TYPE_META = {
  flight: { Icon: Plane, label: "Flight" },
  rail: { Icon: Train, label: "Rail" },
  train: { Icon: Train, label: "Rail" },
  transfer: { Icon: Car, label: "Transfer" },
  driver: { Icon: Car, label: "Private driver" },
  ferry: { Icon: Ship, label: "Ferry" },
  transport: { Icon: Car, label: "Transport" },
};

function fmtTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

function fmtTimeShort(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

function copyText(text) {
  if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => {});
}

// Quiet inline terminal/gate display: "T3 · Gate B22"
function TerminalGate({ terminal, gate, prefix }) {
  if (!terminal && !gate) return null;
  const parts = [];
  if (terminal) parts.push(`T${terminal}`);
  if (gate && gate !== "—") parts.push(`Gate ${gate}`);
  if (parts.length === 0) return null;
  return (
    <span className="font-mono text-[11px] tracking-[0.04em] text-[hsl(var(--foreground-muted))]">
      {prefix ? `${prefix} · ` : ""}{parts.join(" · ")}
    </span>
  );
}

function AttachmentChips({ attachments, testidPrefix }) {
  if (!attachments?.length) return null;
  return (
    <div className="mt-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-2">
        Attachments
      </p>
      <div className="flex flex-wrap gap-2">
        {attachments.map((a) => (
          <a
            key={a.id}
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[hsl(var(--border))] bg-white/40 font-mono text-[11px] text-[hsl(var(--foreground-secondary))] hover:border-[hsl(var(--accent-gold))] hover:text-[hsl(var(--accent-gold))] transition-colors"
            data-testid={`${testidPrefix}-attachment-${a.id}`}
          >
            <Paperclip className="size-3" strokeWidth={1.5} />
            {a.name}
          </a>
        ))}
      </div>
    </div>
  );
}

// ─── Segment row (sub-card under a multi-segment flight parent) ──────
function SegmentRow({ s, index }) {
  return (
    <div
      className="pl-5 pr-4 py-3 sm:pl-6 sm:pr-5 border-t border-[rgba(0,0,0,0.04)] bg-[hsl(var(--background-paper)/0.4)]"
      data-testid={`transport-segment-${s.id}`}
      data-segment-order={s.segment_order || index + 1}
    >
      <div className="flex items-start gap-3.5">
        <span className="inline-flex items-center justify-center size-6 rounded-full bg-white border border-[rgba(0,0,0,0.06)] font-mono text-[10px] text-[hsl(var(--foreground-muted))] flex-shrink-0 tabular-nums">
          {(s.segment_order || index + 1).toString().padStart(2, "0")}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="font-serif text-[15px] tracking-tight">
              {s.origin_iata}
              <ArrowRight className="inline size-3 mx-1.5 -translate-y-px text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
              {s.destination_iata}
            </span>
            <span className="font-mono text-[11px] text-[hsl(var(--foreground-muted))] tabular-nums">
              {s.airline} {s.flight_number}
            </span>
          </div>
          <p className="mt-1 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
            {fmtTimeShort(s.depart_at)} → {fmtTimeShort(s.arrive_at)}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
            <TerminalGate terminal={s.origin_terminal} gate={s.origin_gate} prefix="Dep" />
            <TerminalGate terminal={s.destination_terminal} gate={s.destination_gate} prefix="Arr" />
            {s.seat_notes && (
              <span className="font-mono text-[11px] text-[hsl(var(--foreground-muted))]">Seats {s.seat_notes}</span>
            )}
          </div>
          {s.attachments?.length > 0 && (
            <AttachmentChips attachments={s.attachments} testidPrefix={`segment-${s.id}`} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Parent / single-card ────────────────────────────────────────────
function TransportCard({ b, segments }) {
  const [open, setOpen] = useState(false);
  const meta = TYPE_META[(b.booking_type || "").toLowerCase()] || TYPE_META.transfer;
  const { Icon, label } = meta;
  const hasSegments = segments?.length > 0;

  const title = (() => {
    if (b.booking_type === "flight") {
      return `${b.origin_iata || ""} → ${b.destination_iata || ""}`.trim();
    }
    if (b.pickup_location && b.dropoff_location) {
      return `${b.pickup_location} → ${b.dropoff_location}`;
    }
    return b.description || b.vendor || "Transport";
  })();

  const when = b.depart_at || b.pickup_at || b.start_date;
  const segmentCount = hasSegments ? segments.length : 0;
  // Hide single-card inline flight_number when it's a parent of segments
  const showFlightNum = b.flight_number && !hasSegments;

  return (
    <motion.article
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-md bg-white/65 backdrop-blur-[2px] border overflow-hidden transition-opacity ${b._next ? "border-[hsl(var(--accent-gold))]/50 ring-1 ring-[hsl(var(--accent-gold))]/25" : "border-[rgba(0,0,0,0.06)]"} ${b._state === "completed" ? "opacity-60" : ""}`}
      data-testid={`transport-card-${b.id}`}
      data-booking-type={b.booking_type}
      data-multi-segment={hasSegments || undefined}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-5 py-4 sm:px-6 sm:py-5 flex items-start gap-4 hover:bg-[hsl(var(--accent-gold)/0.04)] transition-colors"
        aria-expanded={open}
      >
        <span className="inline-flex items-center justify-center size-9 rounded-full bg-[hsl(var(--background-paper))] flex-shrink-0">
          <Icon className="size-4 text-[hsl(var(--foreground-muted))]" strokeWidth={1.4} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              {label}
              {b.airline ? ` · ${b.airline}${showFlightNum ? ` ${b.flight_number}` : ""}` : b.vendor ? ` · ${b.vendor}` : ""}
              {hasSegments ? ` · ${segmentCount} segments` : ""}
            </span>
            <StateChip item={b} />
          </span>
          <h3 className="mt-1 font-serif text-[18px] sm:text-[20px] leading-tight text-[hsl(var(--foreground))] tracking-tight">
            {title}
          </h3>
          {when && (
            <p className="mt-1.5 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))] flex items-center gap-1.5">
              <Clock className="size-3" strokeWidth={1.5} />
              {fmtTime(when)}
            </p>
          )}
        </span>
        <ChevronDown
          className={`size-4 text-[hsl(var(--foreground-muted))] flex-shrink-0 mt-1.5 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={1.5}
        />
      </button>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
          className="border-t border-[rgba(0,0,0,0.05)]"
        >
          {b.travel_reminder && (
            <div className="mx-5 mt-4 sm:mx-6 rounded-md border border-[hsl(var(--accent-gold))]/35 bg-[hsl(var(--accent-gold))]/[0.06] px-4 py-3 flex items-start gap-2.5" data-testid={`transport-reminder-${b.id}`}>
              <AlertCircle className="size-4 text-[hsl(var(--accent-gold))] mt-0.5 shrink-0" strokeWidth={1.6} />
              <p className="font-accent italic text-[13px] leading-relaxed text-[hsl(var(--foreground-secondary))]">{b.travel_reminder}</p>
            </div>
          )}
          {/* Multi-segment chain — quiet sub-rows */}
          {hasSegments && (
            <div data-testid={`transport-segments-${b.id}`}>
              {segments.map((s, i) => <SegmentRow key={s.id} s={s} index={i} />)}
            </div>
          )}

          {/* Single-card details (when not multi-segment) */}
          {!hasSegments && (
            <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-4">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-[13.5px]">
                {b.arrive_at && (
                  <div>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Arrives</dt>
                    <dd className="mt-0.5 font-serif text-[15px]">{fmtTime(b.arrive_at)}</dd>
                  </div>
                )}
                {(b.origin_terminal || b.origin_gate) && (
                  <div>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Departure</dt>
                    <dd className="mt-0.5"><TerminalGate terminal={b.origin_terminal} gate={b.origin_gate} /></dd>
                  </div>
                )}
                {(b.destination_terminal || b.destination_gate) && (
                  <div>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Arrival</dt>
                    <dd className="mt-0.5"><TerminalGate terminal={b.destination_terminal} gate={b.destination_gate} /></dd>
                  </div>
                )}
                {b.confirmation_number && (
                  <div>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Confirmation</dt>
                    <dd className="mt-0.5 font-mono text-[13px] flex items-center gap-2 tabular-nums">
                      <span>{b.confirmation_number}</span>
                      <button
                        type="button"
                        onClick={() => copyText(b.confirmation_number)}
                        className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]"
                        title="Copy"
                        data-testid={`copy-${b.id}`}
                      >
                        <Copy className="size-3" strokeWidth={1.5} />
                      </button>
                    </dd>
                  </div>
                )}
                {b.seat_notes && (
                  <div>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Seats</dt>
                    <dd className="mt-0.5 font-serif text-[15px]">{b.seat_notes}</dd>
                  </div>
                )}
                {b.vehicle_type && (
                  <div>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Vehicle</dt>
                    <dd className="mt-0.5 font-serif text-[15px]">{b.vehicle_type}</dd>
                  </div>
                )}
                {b.phone && (
                  <div>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Contact</dt>
                    <dd className="mt-0.5">
                      <a href={`tel:${b.phone}`} className="font-mono text-[13px] inline-flex items-center gap-1.5 text-[hsl(var(--accent-gold))] hover:underline">
                        <Phone className="size-3" strokeWidth={1.5} />{b.phone}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
              {b.notes && (
                <p className="mt-4 font-accent italic text-[13.5px] text-[hsl(var(--foreground-secondary))] border-l-2 border-[hsl(var(--accent-gold)/0.4)] pl-3">
                  {b.notes}
                </p>
              )}
              <AttachmentChips attachments={b.attachments} testidPrefix={`transport-${b.id}`} />
            </div>
          )}

          {/* For multi-segment parents: show confirmation + notes + parent attachments below segments */}
          {hasSegments && (
            <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-4 border-t border-[rgba(0,0,0,0.04)]">
              <div className="flex flex-wrap gap-x-6 gap-y-3 text-[13.5px]">
                {b.confirmation_number && (
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Booking ref</p>
                    <p className="mt-0.5 font-mono text-[13px] tabular-nums inline-flex items-center gap-2">
                      <span>{b.confirmation_number}</span>
                      <button onClick={() => copyText(b.confirmation_number)} className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]" title="Copy" data-testid={`copy-${b.id}`}>
                        <Copy className="size-3" strokeWidth={1.5} />
                      </button>
                    </p>
                  </div>
                )}
              </div>
              {b.notes && (
                <p className="mt-4 font-accent italic text-[13.5px] text-[hsl(var(--foreground-secondary))] border-l-2 border-[hsl(var(--accent-gold)/0.4)] pl-3">
                  {b.notes}
                </p>
              )}
              <AttachmentChips attachments={b.attachments} testidPrefix={`transport-${b.id}`} />
            </div>
          )}
        </motion.div>
      )}
    </motion.article>
  );
}

export default function TransportationShelf({ items = [] }) {
  const { arrivals, departures, ground } = useMemo(() => {
    const byId = new Map();
    const segmentsByParent = new Map();
    for (const b of items) {
      byId.set(b.id, b);
      if (b.parent_booking_id) {
        const arr = segmentsByParent.get(b.parent_booking_id) || [];
        arr.push(b);
        segmentsByParent.set(b.parent_booking_id, arr);
      }
    }
    for (const arr of segmentsByParent.values()) {
      arr.sort((a, b) => (a.segment_order || 0) - (b.segment_order || 0));
    }
    const top = items
      .filter((b) => !b.parent_booking_id || !byId.has(b.parent_booking_id))
      .map((b) => ({ ...b, _segments: segmentsByParent.get(b.id) || [] }));

    const flights = top.filter((b) => (b.booking_type || "").toLowerCase() === "flight");
    const ground = top.filter((b) => (b.booking_type || "").toLowerCase() !== "flight");
    // Arrival = lands in Lisbon/Portugal; Departing = leaves from Lisbon.
    const isDeparture = (b) => (b.origin_iata || "").toUpperCase() === "LIS";
    const getStart = (b) => b.depart_at || b.pickup_at || b.start_date;
    const getEnd = (b) => b.arrive_at || b.dropoff_at || b.end_date || getStart(b);
    return {
      arrivals: withEventStates(flights.filter((b) => !isDeparture(b)), getStart, getEnd),
      departures: withEventStates(flights.filter(isDeparture), getStart, getEnd),
      ground: withEventStates(ground, getStart, getEnd),
    };
  }, [items]);

  const total = arrivals.length + departures.length + ground.length;
  if (total === 0) return null;

  const Group = ({ label, list, testid }) => {
    const allCompleted = list.length > 0 && list.every((b) => b._state === "completed");
    const [open, setOpen] = React.useState(!allCompleted);
    if (list.length === 0) return null;
    return (
      <div className="space-y-3 sm:space-y-4" data-testid={testid}>
        <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`${testid}-toggle`}
          className="w-full flex items-center justify-between gap-2 text-left">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))] flex items-center gap-2">
            {label}
            {allCompleted && <span className="text-[hsl(var(--foreground-muted))]">· Completed</span>}
          </span>
          <ChevronDown className={`size-4 text-[hsl(var(--foreground-muted))] transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
        </button>
        {open && list.map((b) => <TransportCard key={b.id} b={b} segments={b._segments} />)}
      </div>
    );
  };

  return (
    <section id="transportation" data-testid="essentials-transportation" className="scroll-mt-24">
      <header className="mb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          Transportation
        </p>
        <h2 className="mt-1.5 font-serif text-[22px] sm:text-[26px] leading-tight tracking-tight">
          How you'll move.
        </h2>
      </header>
      <div className="space-y-6">
        <Group label="Arrival Flights" list={arrivals} testid="transport-group-arrivals" />
        <Group label="Departing Flights" list={departures} testid="transport-group-departures" />
        <Group label="Ground Transportation" list={ground} testid="transport-group-ground" />
      </div>
    </section>
  );
}
